/**
 * Passenger List component.
 *
 * Allows adding and removing passenger names, enforcing a maximum of
 * MAX_PASSENGERS (5). Emits a custom 'passengers-change' event on updates.
 *
 * Exports pure helper functions for testability.
 *
 * @module passenger-list
 * Requirements: 10.4, 10.5
 */

import { MAX_PASSENGERS } from '../utils/constants.js';

// ---------------------------------------------------------------------------
// Pure helper functions (exported for testing)
// ---------------------------------------------------------------------------

/**
 * Attempts to add a passenger to the list.
 * Returns a new array if successful, or null if the list is full or name is invalid.
 *
 * @param {string[]} passengers - Current passenger list
 * @param {string} name - Passenger name to add
 * @returns {{ passengers: string[], error: string|null }}
 */
export function addPassenger(passengers, name) {
  const trimmed = (typeof name === 'string') ? name.trim() : '';
  if (trimmed === '') {
    return { passengers, error: 'Passenger name must not be empty' };
  }
  if (passengers.length >= MAX_PASSENGERS) {
    return { passengers, error: `Maximum of ${MAX_PASSENGERS} passengers reached` };
  }
  return { passengers: [...passengers, trimmed], error: null };
}

/**
 * Removes a passenger at the given index.
 *
 * @param {string[]} passengers - Current passenger list
 * @param {number} index - Index to remove
 * @returns {string[]} New passenger list
 */
export function removePassenger(passengers, index) {
  if (index < 0 || index >= passengers.length) return [...passengers];
  return passengers.filter((_, i) => i !== index);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Creates a Passenger List component.
 *
 * @param {Object} [config={}]
 * @param {string[]} [config.initialPassengers=[]] - Pre-populated passengers
 * @param {function} [config.onChange] - Callback when list changes: (passengers) => void
 * @returns {HTMLElement}
 */
export function createPassengerList({ initialPassengers, onChange } = {}) {
  let passengers = Array.isArray(initialPassengers) ? [...initialPassengers] : [];

  const container = document.createElement('div');
  container.className = 'passenger-list';

  // Title
  const title = document.createElement('h4');
  title.className = 'passenger-list__title';
  title.textContent = 'Passengers';
  container.appendChild(title);

  // List container
  const listEl = document.createElement('ul');
  listEl.className = 'passenger-list__items';
  container.appendChild(listEl);

  // Message area
  const messageEl = document.createElement('div');
  messageEl.className = 'passenger-list__message';
  messageEl.setAttribute('role', 'status');
  container.appendChild(messageEl);

  // Input row
  const inputRow = document.createElement('div');
  inputRow.className = 'passenger-list__input-row';

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'passenger-list__input';
  input.placeholder = 'Passenger name';
  input.setAttribute('aria-label', 'Passenger name');
  inputRow.appendChild(input);

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'passenger-list__add-btn';
  addBtn.textContent = 'Add';
  inputRow.appendChild(addBtn);

  container.appendChild(inputRow);

  // Counter
  const counterEl = document.createElement('span');
  counterEl.className = 'passenger-list__counter';
  container.appendChild(counterEl);

  // --- Rendering ---

  function render() {
    listEl.innerHTML = '';
    for (let i = 0; i < passengers.length; i++) {
      const li = document.createElement('li');
      li.className = 'passenger-list__item';

      const nameSpan = document.createElement('span');
      nameSpan.className = 'passenger-list__name';
      nameSpan.textContent = passengers[i];
      li.appendChild(nameSpan);

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'passenger-list__remove-btn';
      removeBtn.textContent = 'Remove';
      removeBtn.setAttribute('aria-label', `Remove ${passengers[i]}`);
      removeBtn.addEventListener('click', () => {
        passengers = removePassenger(passengers, i);
        render();
        notifyChange();
      });
      li.appendChild(removeBtn);

      listEl.appendChild(li);
    }

    counterEl.textContent = `${passengers.length} / ${MAX_PASSENGERS}`;
    addBtn.disabled = passengers.length >= MAX_PASSENGERS;
    input.disabled = passengers.length >= MAX_PASSENGERS;
  }

  function notifyChange() {
    messageEl.textContent = '';
    container.dispatchEvent(new CustomEvent('passengers-change', {
      bubbles: true,
      detail: { passengers: [...passengers] },
    }));
    if (typeof onChange === 'function') {
      onChange([...passengers]);
    }
  }

  // --- Event handlers ---

  addBtn.addEventListener('click', () => {
    const result = addPassenger(passengers, input.value);
    if (result.error) {
      messageEl.textContent = result.error;
    } else {
      passengers = result.passengers;
      input.value = '';
      messageEl.textContent = '';
      render();
      notifyChange();
    }
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addBtn.click();
    }
  });

  // Initial render
  render();

  /** Get the current passenger list. */
  container.getPassengers = () => [...passengers];

  return container;
}
